import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, signal } from "@angular/core"
import { FormControl, FormGroup, Validators } from "@angular/forms"
import { CONTACT_TITLE_OPTIONS } from "src/app/meta/contact-title-options.const"
import { BillingDataService } from "src/app/services/billing-data.service"

@Component({
  selector: "ow-billing-address-form",
  templateUrl: "./billing-address-form.component.html"
})
export class BillingAddressFormComponent implements OnInit, OnDestroy {

  @Output() updateValidity = new EventEmitter<boolean>(false)
  
  private readonly billinDataService = inject(BillingDataService)

  public readonly TITLE_OPTIONS = CONTACT_TITLE_OPTIONS

  public readonly form = new FormGroup({
    inherit: new FormControl<boolean>(this.billinDataService.getBillingAddres().inherit),
    title: new FormControl<string>(this.billinDataService.getBillingAddres().title),
    firstName: new FormControl<string>(this.billinDataService.getBillingAddres().firstName),
    lastName: new FormControl<string>(this.billinDataService.getBillingAddres().lastName),
    streetName: new FormControl<string>(this.billinDataService.getBillingAddres().streetName),
    streetNumber: new FormControl<string>(this.billinDataService.getBillingAddres().streetNumber),
    postalCode: new FormControl<string>(this.billinDataService.getBillingAddres().postalCode),
    city: new FormControl<string>(this.billinDataService.getBillingAddres().city)
  })

  public readonly showAddressFields = signal<boolean>(false)

  private readonly subscriptions = [
    this.form.controls.inherit.valueChanges.subscribe((value) => {
      this.onInheritChange()
      if (this.form.controls.inherit.valid) {
        this.billinDataService.setBillingAddress({ inherit: value! })
      }
    }),
    this.form.controls.title.valueChanges.subscribe((value) => {
      if (this.form.controls.title.valid) {
        this.billinDataService.setBillingAddress({ title: value! })
      }
    }),
    this.form.controls.firstName.valueChanges.subscribe((value) => {
      if (this.form.controls.firstName.valid) {
        this.billinDataService.setBillingAddress({ firstName: value! })
      }
    }),
    this.form.controls.lastName.valueChanges.subscribe((value) => {
      if (this.form.controls.lastName.valid) {
        this.billinDataService.setBillingAddress({ lastName: value! })
      }
    }),
    this.form.controls.streetName.valueChanges.subscribe((value) => {
      if (this.form.controls.streetName.valid) {
        this.billinDataService.setBillingAddress({ streetName: value! })
      }
    }),
    this.form.controls.streetNumber.valueChanges.subscribe((value) => {
      if (this.form.controls.streetNumber.valid) {
        this.billinDataService.setBillingAddress({ streetNumber: value! })
      }
    }),
    this.form.controls.postalCode.valueChanges.subscribe((value) => {
      if (this.form.controls.postalCode.valid) {
        this.billinDataService.setBillingAddress({ postalCode: value! })
      }
    }),
    this.form.controls.city.valueChanges.subscribe((value) => {
      if (this.form.controls.city.valid) {
        this.billinDataService.setBillingAddress({ city: value! })
      }
    }),
    this.form.statusChanges.subscribe(() => {
      this.updateValidity.emit(this.form.valid)
    })
  ]

  ngOnInit(): void {
    this.onInheritChange()
    this.updateValidity.emit(this.form.valid)
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe()
    })
  }

  private onInheritChange(): void {
    this.showAddressFields.set(!this.form.controls.inherit.value)
    if (this.form.controls.inherit.value) {
      this.form.controls.title.clearValidators()
      this.form.controls.firstName.clearValidators()
      this.form.controls.lastName.clearValidators()
      this.form.controls.streetName.clearValidators()
      this.form.controls.streetNumber.clearValidators()
      this.form.controls.postalCode.clearValidators()
      this.form.controls.city.clearValidators()
    } else {
      this.form.controls.title.addValidators(Validators.required)
      this.form.controls.firstName.addValidators([Validators.required, Validators.maxLength(24)])
      this.form.controls.lastName.addValidators([Validators.required, Validators.maxLength(24)])
      this.form.controls.streetName.addValidators(Validators.required)
      this.form.controls.streetNumber.addValidators(Validators.required)
      this.form.controls.postalCode.addValidators(Validators.required)
      this.form.controls.city.addValidators(Validators.required)
    }
    this.form.controls.title.updateValueAndValidity()
    this.form.controls.firstName.updateValueAndValidity()
    this.form.controls.lastName.updateValueAndValidity()
    this.form.controls.streetName.updateValueAndValidity()
    this.form.controls.streetNumber.updateValueAndValidity()
    this.form.controls.postalCode.updateValueAndValidity()
    this.form.controls.city.updateValueAndValidity()
  }
}
